// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '@environment';
import { Column, DataRow } from '@lfx/shared/interfaces';
import { CompanyService } from '@lfx/core/services';
import {
  MetricChartEntityService,
  MetricChartData,
  MetricChartApiResponse
} from './metric-chart-base.entity';

/**
 * Event sponsorship data model from SQL query (API returns uppercase keys)
 */
export interface EventSponsorshipData {
  EVENT_NAME: string;
  EVENT_START_DATE: string;
  AUDIENCE_REACHED: number;
  SPONSORSHIP_TIER: string;
  CALCULATED_REACH: number;
  PROJECT_SLUG: string;
  ACCOUNT_ID: string;
}

/**
 * Event sponsorship summary data model from SQL query
 */
export interface EventSponsorshipSummaryData {
  totalAttendeesReached: number;
}

/**
 * Entity service for event sponsorships metric type
 */
@Injectable({
  providedIn: 'root'
})
export class EventSponsorshipsMetricEntity implements MetricChartEntityService {
  private readonly http = inject(HttpClient);
  private readonly companyService = inject(CompanyService);

  private readonly config = {
    definition: "Count of attendees reached through foundation's LF event sponsorship.",
    columns: [
      { name: 'Event sponsored', prop: 'eventName' },
      { name: 'Attendee count', prop: 'attendeeCount' },
      { name: 'Sponsorship level', prop: 'sponsorshipLevel' },
      { name: 'Calculated reach', prop: 'calculatedReach' }
    ],
    summaryColumns: [
      { name: 'Definition', prop: 'definition' },
      { name: 'Total attendees for events', prop: 'totalCount' },
      { name: 'Data source', prop: 'dataSource' }
    ],
    dataSource: 'LFX'
  };

  /**
   * Get columns for event sponsorships table
   * Based on UI requirements for event sponsorships display
   */
  getColumns(): Column[] {
    return this.config.columns;
  }

  /**
   * Get API endpoint for event sponsorships
   */
  getEndpoint(): string {
    return `${environment.apiUrl}/event-sponsorships`;
  }

  /**
   * Fetch event sponsorships data from backend
   */
  fetchData(params: MetricChartData): Observable<DataRow[]> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('accountId', company.salesforceB2BAccountId)
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<MetricChartApiResponse<EventSponsorshipData>>(
          this.getEndpoint(),
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch event sponsorships data');
            }

            return response.data.map(sponsorship => this.transformToDataRow(sponsorship));
          }),
          catchError(error => {
            console.error('Error fetching event sponsorships data:', error);
            return of([]);
          })
        );
      }),
      catchError(error => {
        console.error('Error in event sponsorships entity service:', error);
        return of([]);
      })
    );
  }

  /**
   * Transform event sponsorship data to DataRow format for the table
   */
  private transformToDataRow(sponsorship: EventSponsorshipData): DataRow {
    return {
      eventName: { text: sponsorship.EVENT_NAME },
      attendeeCount: { text: this.formatNumber(sponsorship.AUDIENCE_REACHED) },
      sponsorshipLevel: { text: sponsorship.SPONSORSHIP_TIER },
      calculatedReach: { text: this.formatNumber(sponsorship.CALCULATED_REACH) }
    };
  }

  /**
   * Fetch event sponsorships summary data from backend
   */
  getSummaryData(params: MetricChartData): Observable<{columns: Column[], data: DataRow[]}> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<{success: boolean; message?: string; data: EventSponsorshipSummaryData}>(
          `${this.getEndpoint()}/summary`,
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch event sponsorships summary');
            }

            // Define columns for summary table
            const summaryColumns: Column[] = this.config.summaryColumns;

            // Transform API response to summary table format
            const summaryData: DataRow[] = [
              {
                definition: { text: this.config.definition },
                totalCount: { text: this.formatNumber(response.data.totalAttendeesReached) },
                dataSource: { text: this.config.dataSource }
              }
            ];

            return { columns: summaryColumns, data: summaryData };
          }),
          catchError(error => {
            console.error('Error fetching event sponsorships summary:', error);
            // Return fallback data on error
            const fallbackColumns: Column[] = this.config.summaryColumns;
            const fallbackData: DataRow[] = [
              {
                definition: { text: this.config.definition },
                totalCount: { text: 'N/A' },
                dataSource: { text: this.config.dataSource }
              }
            ];
            return of({ columns: fallbackColumns, data: fallbackData });
          })
        );
      }),
      catchError(error => {
        console.error('Error in event sponsorships summary entity service:', error);
        // Return fallback data
        const fallbackColumns: Column[] = this.config.summaryColumns;
        const fallbackData: DataRow[] = [
          {
            definition: { text: this.config.definition },
            totalCount: { text: 'N/A' },
            dataSource: { text: this.config.dataSource }
          }
        ];
        return of({ columns: fallbackColumns, data: fallbackData });
      })
    );
  }

  /**
   * Format number for display with thousand separators
   */
  private formatNumber(value: number): string {
    if (value === null || value === undefined) {
      return '0';
    }
    return value.toLocaleString('en-US');
  }
}

